import React, { useState } from "react"

import "./listaTarjetas.css"

/*
	Lista en tarjetas para telefono.

	Las tablas de la app tienen entre ocho y catorce columnas: en un telefono se
	vuelven una rejilla que hay que arrastrar de lado. Esto es la misma idea que
	ya se uso en Asistencias, sacada a un componente para que Facturas, Alumnos y
	Personal se vean igual entre si. (Esteban, 2026-09-16)

	La vista de computadora no se toca: ahi la tabla sigue siendo lo correcto.
*/

export type TonoChip = "naranja" | "verde" | "ambar" | "gris"

export interface CampoTarjeta
{
	etiqueta : string
	valor    : React.ReactNode
	/** Ocupa las dos columnas de la rejilla; para textos largos. */
	ancho?   : boolean
}

export interface Tarjeta
{
	clave       : string | number
	titulo      : React.ReactNode
	subtitulo?  : React.ReactNode
	chip?       : {texto : string, tono : TonoChip}
	/** Marca de aviso junto al titulo, como "Debe". */
	marca?      : {texto : string, tono : TonoChip}
	campos      : CampoTarjeta[]
	/** La primera va en naranja; las demas de contorno. */
	acciones?   : {texto : string, onClick : () => void, principal? : boolean, disabled? : boolean}[]
	/** Lo que no cabe en los botones: se llega por el "...". */
	menu?       : {texto : string, onClick : () => void}[]
	/** Apaga la tarjeta: lo que ya no corre. */
	apagada?    : boolean
	/** Lo que cuelga de la tarjeta: los modulos de un curso, un aviso. */
	pie?        : React.ReactNode
}

interface Props
{
	tarjetas : Tarjeta[]
	vacio?   : string
}

const ListaTarjetas = ({tarjetas, vacio} : Props) =>
{
	//Que tarjeta tiene abierto su menu; null = ninguna.
	const [menuAbierto, setMenuAbierto] = useState<string | number | null>(null)

	if(!tarjetas || tarjetas.length === 0)
	{
		return <p className="waLtVacio">
			{vacio || "No hay nada que mostrar."}
		</p>
	}

	return <div className="waLt">
		{
			tarjetas.map(tarjeta => <article
				className={tarjeta.apagada ? "waLtTarjeta apagada" : "waLtTarjeta"}
				key={tarjeta.clave}
			>
				<header className="waLtCabecera">
					<span className="waLtTitulo">
						{tarjeta.titulo}
						{
							!tarjeta.marca ? null :
								<span className={"waLtMarca " + tarjeta.marca.tono}>
									{tarjeta.marca.texto}
								</span>
						}
					</span>
					{
						!tarjeta.chip ? null :
							<span className={"waLtChip " + tarjeta.chip.tono}>
								{tarjeta.chip.texto}
							</span>
					}
				</header>

				{
					!tarjeta.subtitulo ? null :
						<p className="waLtSubtitulo">
							{tarjeta.subtitulo}
						</p>
				}

				{
					tarjeta.campos.length === 0 ? null :
						<dl className="waLtCampos">
							{
								tarjeta.campos
									.filter(campo => campo.valor !== null && campo.valor !== undefined && campo.valor !== "")
									.map((campo, indice) => <div
										className={campo.ancho ? "waLtCampo ancho" : "waLtCampo"}
										key={tarjeta.clave + "_" + indice}
									>
										<dt>{campo.etiqueta}</dt>
										<dd>{campo.valor}</dd>
									</div>)
							}
						</dl>
				}

				{
					!tarjeta.acciones || tarjeta.acciones.length === 0 ? null :
						<div className="waLtAcciones">
							{
								tarjeta.acciones.map((accion, i) => <button
									key={tarjeta.clave + "_a" + i}
									type="button"
									className={accion.principal ? "waLtAccion principal" : "waLtAccion"}
									disabled={accion.disabled}
									onClick={accion.onClick}
								>
									{accion.texto}
								</button>)
							}
							{
								!tarjeta.menu || tarjeta.menu.length === 0 ? null :
									<div className="waLtMenu">
										<button
											type="button"
											className="waLtAccion waLtPuntos"
											aria-label="Más acciones"
											aria-expanded={menuAbierto === tarjeta.clave}
											onClick={() => setMenuAbierto(menuAbierto === tarjeta.clave ? null : tarjeta.clave)}
										>
											•••
										</button>
										{
											menuAbierto !== tarjeta.clave ? null :
												<>
													<div className="waMenuFuera" onClick={() => setMenuAbierto(null)} />
													<div className="waMenuPanel">
														{
															tarjeta.menu.map((item, i) => <button
																key={tarjeta.clave + "_m" + i}
																type="button"
																onClick={() => { setMenuAbierto(null); item.onClick() }}
															>
																{item.texto}
															</button>)
														}
													</div>
												</>
										}
									</div>
							}
						</div>
				}

				{
					!tarjeta.pie ? null :
						<div className="waLtPie">
							{tarjeta.pie}
						</div>
				}
			</article>)
		}
	</div>
}

export default ListaTarjetas
